// GUI §2 design tokens. Values are the contract; `aaContrast` is the spec gate
// ("critical alert surfaces meet WCAG AA in both modes"). `--aid` is picked here
// (deep teal-green, AA on both papers) per the spec's "to be picked" note.

export interface PaperTokens {
  paper: string;
  paper2: string;
  ink: string;
  ink2: string;
  ink3: string;
  line: string;
  accent: string;
  advisory: string;
  aid: string;
  contained: string;
}

export const TOKENS: { light: PaperTokens; dark: PaperTokens } = {
  light: {
    paper: "#ece6d6",
    paper2: "#e5dfce",
    ink: "#1a1714",
    ink2: "#5c554b",
    ink3: "#938b7e",
    line: "#cfc7b4",
    accent: "#c0392b", // terracotta, darkened from mockup #d14a3a for AA-large on cream
    advisory: "#9a6e1f", // ochre, darkened for AA-large on cream
    aid: "#0c6b5a", // deep teal-green, AA on cream
    contained: "#8a6038", // muted tan, darkened for legibility
  },
  dark: {
    paper: "#1c1a16",
    paper2: "#24211b",
    ink: "#efe9da",
    ink2: "#b8b1a1",
    ink3: "#8a8475",
    line: "#3a352c",
    accent: "#e8806f",
    advisory: "#d6a64e",
    aid: "#4fbfa6",
    contained: "#caa078",
  },
};

// sRGB relative luminance (WCAG 2.1).
function luminance(hex: string): number {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) throw new Error(`bad hex: ${hex}`);
  const chan = (h: string): number => {
    const c = parseInt(h, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * chan(m[1]) + 0.7152 * chan(m[2]) + 0.0722 * chan(m[3]);
}

/** WCAG 2.1 contrast ratio between two hex colors (1..21). */
export function aaContrast(fg: string, bg: string): number {
  const a = luminance(fg);
  const b = luminance(bg);
  const [hi, lo] = a >= b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}
